import { useEffect, forwardRef } from "react"
import { Draft } from "immer"
import { useImmerReducer } from "use-immer"
import { NavigateFn } from "@reach/router"
import { nanoid } from "nanoid"
import { RootContextType, NavigationAction, NavigationState, NavigationOrientation } from "./types"
import { Flex } from "../Flex"
import { createContext, sanitizeParams, paramWhitelists } from "../utils"
import { CSSFn } from "../styles"

const [Provider, useContext] = createContext<RootContextType>(`Navigation`)

export const useNavigation = useContext

type RootOwnProps = {
  navigateFn: NavigateFn
  navTriggered: boolean
  setNavTriggered: (triggered: boolean) => void
  onValueChange?: (value: string) => void
  orientation?: NavigationOrientation
  css?: CSSFn
}

export type RootProps = React.ComponentPropsWithRef<typeof Flex> & RootOwnProps

export const Root: React.FC<RootProps> = forwardRef(
  (
    { navigateFn, onValueChange, orientation, navTriggered, setNavTriggered, ...containerProps }: RootProps,
    forwardedRef
  ) => {
    const [state, dispatch] = useImmerReducer<NavigationState, NavigationAction>(reducer, {
      value: ``,
      transaction: null,
      orientation: orientation ?? `vertical`,
    })
    useEffect(() => {
      if (navTriggered) {
        onValueChange?.(state.value.replace(`/`, ``))
        navigateFn(state.value)
        setNavTriggered(false)
      }
    }, [state.value, navigateFn, onValueChange, navTriggered, setNavTriggered])

    useEffect(() => {
      const { pathname, search } = window.location

      const regexp = new RegExp(`${state.value}`)
      const match = pathname.match(regexp)
      if (match !== null) {
        ;(async () => {
          const searchParams = await sanitizeParams(new URLSearchParams(search), paramWhitelists.trips) // TODO: Separate whitelist for each route
          dispatch({
            type: `navigate`,
            value: `${pathname}${searchParams}`,
          })
        })()
      }
    }, [dispatch, state.value])

    return (
      <Provider state={state} dispatch={dispatch}>
        <Flex
          ref={forwardedRef}
          flexDirection={state.orientation === `horizontal` ? `row` : `column`}
          flexGrow={0}
          width="100%"
          {...containerProps}
        />
      </Provider>
    )
  }
)

Root.defaultProps = {
  onValueChange: undefined,
  orientation: undefined,
  css: undefined,
}

function reducer(state: Draft<NavigationState>, action: NavigationAction): NavigationState {
  switch (action.type) {
    case `navigate`:
      return {
        ...state,
        transaction: nanoid(),
        value: action.value,
      }
    default:
      return state
  }
}
